import { supabase } from '../lib/supabaseClient';

// fetch wrapper that attaches the Supabase access token. Throws on non-2xx.
async function authedFetch(path, options = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('نشست شما منقضی شده است. دوباره وارد شوید.');
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j.error || j.detail || ''; } catch {}
    throw new Error(detail || `خطای سرور (${res.status})`);
  }
  return res.json();
}

// URLSearchParams stringifies undefined as the literal "undefined", which
// breaks any backend that does truthy-checks ("search=undefined" passes
// `if (search)`). Strip those before constructing the query string so
// callers can spread `{search: debounced || undefined}` without surprises.
function qs(params) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );
  return new URLSearchParams(clean).toString();
}

export const consultantApi = {
  listNotes: (params = {}) =>
    authedFetch('/api/consultant/notes?' + qs(params)),
  createNote: (body) =>
    authedFetch('/api/consultant/notes', { method: 'POST', body: JSON.stringify(body) }),
  updateNote: (id, body) =>
    authedFetch(`/api/consultant/notes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  archiveNote: (id) =>
    authedFetch(`/api/consultant/notes/${id}/archive`, { method: 'POST', body: '{}' }),
  restoreNote: (id) =>
    authedFetch(`/api/consultant/notes/${id}/restore`, { method: 'POST', body: '{}' }),
  // Hard delete — only valid for archived notes (backend enforces). Wipes
  // the row + its revisions (FK cascade); irreversible.
  deleteNote: (id) =>
    authedFetch(`/api/consultant/notes/${id}`, { method: 'DELETE' }),
  listRevisions: (id) =>
    authedFetch(`/api/consultant/notes/${id}/revisions`),
  revertNote: (id, revisionId) =>
    authedFetch(`/api/consultant/notes/${id}/revert`, {
      method: 'POST', body: JSON.stringify({ revision_id: revisionId }),
    }),
  listDocuments: () => authedFetch('/api/consultant/documents'),
  registerDocument: (body) =>
    authedFetch('/api/consultant/documents', { method: 'POST', body: JSON.stringify(body) }),
  listMessages: () => authedFetch('/api/consultant/training/messages'),
  sendMessage: (content) =>
    authedFetch('/api/consultant/training/message', {
      method: 'POST', body: JSON.stringify({ content }),
    }),
  // Async job — returns {job_id, status:'pending'} immediately. The actual
  // merge/flag pass runs in the background server-side; the frontend watches
  // the dreaming_jobs row via supabase realtime for the terminal status.
  runDreaming: (category) =>
    authedFetch('/api/consultant/dreaming', {
      method: 'POST', body: JSON.stringify({ category: category || null }),
    }),
  // Resume-on-mount probe. Returns {job: {id, status, ...} | null}.
  getActiveDreamingJob: () =>
    authedFetch('/api/consultant/dreaming/active'),
  listWaitlist: () => authedFetch('/api/consultant/waitlist'),
  setWaitlistStatus: (id, status) =>
    authedFetch(`/api/consultant/waitlist/${id}/status`, {
      method: 'POST', body: JSON.stringify({ status }),
    }),
  // Voice provider settings (Phase 3.5) — primary/backup are 'openai'|'gemini'.
  getVoiceProvider: () => authedFetch('/api/consultant/settings/voice-provider'),
  setVoiceProvider: (primary, backup) =>
    authedFetch('/api/consultant/settings/voice-provider', {
      method: 'PUT', body: JSON.stringify({ primary, backup }),
    }),

  // Phase 4: voice classroom — admin opens an admin-only realtime voice
  // session with the teaching agent. Returns same envelope as consult
  // realtime/token + an extra ws_ticket field for Gemini transport auth.
  startTeachCall:  () =>
    authedFetch('/api/consultant/teach/realtime/token', { method: 'POST', body: '{}' }),
  attachTeachCall: (sessionId, rtcCallId) =>
    authedFetch('/api/consultant/teach/realtime/attach', {
      method: 'POST', body: JSON.stringify({ session_id: sessionId, rtc_call_id: rtcCallId }),
    }),
  endTeachCall: () =>
    authedFetch('/api/consultant/teach/realtime/end', { method: 'POST', body: '{}' }),
  getTeachStatus: () => authedFetch('/api/consultant/teach/realtime/status'),

  // Phase 4: pending_questions queue admin endpoints.
  listPending:     () => authedFetch('/api/consultant/pending'),
  answerPending:   (id, answer) =>
    authedFetch(`/api/consultant/pending/${id}/answer`, {
      method: 'POST', body: JSON.stringify({ answer }),
    }),
  dismissPending:  (id, reason) =>
    authedFetch(`/api/consultant/pending/${id}/dismiss`, {
      method: 'POST', body: JSON.stringify({ reason: reason || null }),
    }),
};
